import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SAFE_LEVEL_COLORS, pbLevelIndex, safeConfidence } from '@/data/mockSafeData';
import type { FundamentalCatalogItem, PersonalResponse, PersonalGrowthResponse } from '@/api/types';

interface FundamentalPopupProps {
  fundamentalId: string;
  onClose: () => void;
  catalogData: FundamentalCatalogItem[];
  personalData?: PersonalResponse;
  growthData?: PersonalGrowthResponse;
}

const LOCALE_MAP: Record<string, string> = {
  es: 'es-MX', en: 'en-US', pt: 'pt-BR', fr: 'fr-FR', de: 'de-DE', nl: 'nl-NL', ro: 'ro-RO',
};

export function FundamentalPopup({
  fundamentalId,
  onClose,
  catalogData,
  personalData,
  growthData,
}: FundamentalPopupProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const fund   = catalogData.find(f => f.id === fundamentalId);
  const fScore = personalData?.fundamentalScores?.find(f => f.id === fundamentalId);

  if (!fund || !fScore) return null;

  const name        = fund.shortNames?.[lang] ?? fund.shortName ?? fund.names?.[lang] ?? fund.id;
  const description = fund.descriptions?.[lang] ?? fund.description ?? '';
  const color       = fund.color ?? '#342F82';

  const avg    = Math.round(fScore.score * 10) / 10;
  const count  = fScore.evaluations;
  const lvl    = pbLevelIndex(avg);
  const conf   = safeConfidence(count);
  const maxScore = 4;
  const pct    = Math.round((avg / maxScore) * 100);
  const lvlColor = SAFE_LEVEL_COLORS[lvl];

  // Distribution from real aggregate data
  const dist      = fScore.distribution ?? [];
  const distTotal = dist.reduce((a, b) => a + b, 0);
  const distPct   = (i: number) =>
    distTotal > 0 ? Math.round(((dist[i] ?? 0) / distTotal) * 100) : 0;

  // Monthly history — last 6 months from growthData, sorted ascending
  const monthLabel = (m: string) => {
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleString(LOCALE_MAP[lang] ?? 'en-US', { month: 'short' });
  };
  const hist = [...(growthData?.history ?? [])]
    .sort((a, b) => a.month.localeCompare(b.month))
    .slice(-6)
    .map(h => ({
      label: monthLabel(h.month),
      avg: Math.round((h.fundamentals.find(f => f.fundamentalId === fundamentalId)?.average ?? 0) * 10) / 10,
    }));

  // Active daily habits for sub-behavior section
  const habits = fund.dailyHabits.filter(h => h.isActive);

  const scColor = (v: number) =>
    v >= 3.5 ? '#20B15A' : v >= 2.5 ? '#40B8E9' : v >= 1.5 ? '#F08521' : '#E11F26';

  return (
    <div
      ref={overlayRef}
      className="fp-popup-overlay"
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={name}
    >
      <div className="fp-popup">
        <button
          className="fp-popup-close"
          onClick={onClose}
          aria-label={t('common.close')}
          type="button"
        >
          <i className="fas fa-times" />
        </button>

        {/* Header */}
        <div className="fp-popup-header">
          <div className="fp-popup-icon" style={{ background: `${color}18`, color }}>
            {fund.icon
              ? fund.icon.startsWith('fa-')
                ? <i className={`fas ${fund.icon}`} />
                : <img src={fund.icon} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
              : <i className="fas fa-layer-group" />
            }
          </div>
          <div className="fp-popup-title-wrap">
            <h2>{name}</h2>
            <p>{description}</p>
          </div>
          <div className="fp-popup-score-wrap">
            <span className="fp-popup-score" style={{ color: lvlColor }}>{avg}</span>
            <span
              className="fp-popup-score-label"
              style={{ background: `${lvlColor}18`, color: lvlColor }}
            >
              {t(`safe.level${lvl}`)}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="fp-popup-progress">
          <div className="fp-popup-bar-track">
            <div className="fp-popup-bar-fill" style={{ width: `${pct}%`, background: color }} />
          </div>
          <div className="fp-popup-bar-meta">
            <span>{count} {t('safe.popup.peersOpined')} · {conf}</span>
            <span>{avg} / {maxScore}</span>
          </div>
        </div>

        {/* Sub-behaviors (daily habits) */}
        {habits.length > 0 && (
          <div className="fp-popup-section">
            <h3>
              <i className="fas fa-puzzle-piece" />
              {t('safe.popup.subbehaviors')}
            </h3>
            <div className="fp-popup-subbeh">
              {habits.map(h => {
                const hScore  = fScore.dailyHabits?.find(d => d.id === h.id);
                const sbAvg   = hScore ? Math.round(hScore.score * 10) / 10 : null;
                const sbPct   = sbAvg !== null ? Math.round((sbAvg / 4) * 100) : 0;
                const sbColor = sbAvg !== null ? scColor(sbAvg) : '#ccc';
                const sbLvl   = sbAvg !== null ? pbLevelIndex(sbAvg) : 0;
                const hName   = h.names[lang] ?? h.names['en'] ?? h.names['es'] ?? h.id;
                return (
                  <div key={h.id} className="fp-popup-subbeh-row">
                    <div className="fp-popup-subbeh-info">
                      <span className="fp-popup-subbeh-name">{hName}</span>
                      <span className="fp-popup-subbeh-meta">
                        {hScore?.evaluations ?? 0} eval · {sbAvg !== null ? t(`safe.level${sbLvl}`) : '—'}
                      </span>
                    </div>
                    <div className="fp-popup-subbeh-bar-track">
                      <div
                        className="fp-popup-subbeh-bar-fill"
                        style={{ width: `${sbPct}%`, background: sbColor }}
                      />
                    </div>
                    <span className="fp-popup-subbeh-score" style={{ color: sbColor }}>
                      {sbAvg ?? '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Monthly history */}
        {hist.length > 0 && (
          <div className="fp-popup-section">
            <h3>
              <i className="fas fa-chart-line" />
              {t('safe.popup.monthlyHistory')}
            </h3>
            <div className="fp-popup-months">
              {hist.map(h => (
                <div key={h.label} className="fp-popup-month">
                  <div className="fp-popup-month-bar-track">
                    <div
                      className="fp-popup-month-bar-fill"
                      style={{ height: h.avg ? `${(h.avg / 4) * 100}%` : '0%', background: color }}
                    />
                  </div>
                  <span className="fp-popup-month-val">{h.avg || '—'}</span>
                  <span className="fp-popup-month-label">{h.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Distribution */}
        {distTotal >= 3 && (
          <div className="fp-popup-section">
            <h3>
              <i className="fas fa-chart-bar" />
              {t('safe.popup.distribution')}
            </h3>
            <div className="fp-popup-dist">
              {([1, 2, 3, 4] as const).map(l => (
                <div key={l} className="fp-popup-dist-row">
                  <span className="fp-popup-dist-label" style={{ color: SAFE_LEVEL_COLORS[l - 1] }}>
                    {t(`safe.level${l - 1}`)}
                  </span>
                  <div className="fp-popup-dist-bar-track">
                    <div
                      className="fp-popup-dist-bar-fill"
                      style={{ width: `${distPct(l - 1)}%`, background: SAFE_LEVEL_COLORS[l - 1] }}
                    />
                  </div>
                  <span className="fp-popup-dist-pct">{distPct(l - 1)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Behavior scale — global level descriptions (same for all fundamentals) */}
        <div className="fp-popup-section">
          <h3>
            <i className="fas fa-stairs" />
            {t('safe.popup.behaviorScale')}
          </h3>
          <div className="fp-popup-levels">
            {([0, 1, 2, 3] as const).map(i => (
              <div key={i} className={`fp-popup-level${i === lvl ? ' current' : ''}`}>
                <div
                  className="fp-popup-level-num"
                  style={{ background: SAFE_LEVEL_COLORS[i] }}
                >
                  {i + 1}
                </div>
                <div className="fp-popup-level-content">
                  <strong style={{ color: SAFE_LEVEL_COLORS[i] }}>{t(`safe.level${i}`)}</strong>
                  <p>{t(`safe.level${i}desc`)}</p>
                </div>
                {i === lvl && (
                  <span className="fp-popup-level-you">← {t('safe.popup.you')}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
